import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useLogOut } from '../query/useLogOut'
import useGetMe from '../query/useGetMe'
import useGetMePlan from '../query/useGetMePlan'
import useUserStore from '../../store/useUserStore'
import usePrevStore from '../../store/usePrevStore'
import usePlanStore from '../../store/usePlanStore'
import { getLocalStorage, saveLocalStorage, onLogOut } from '../../utils/common'
import { UserType } from '../../utils/common'
import { createLogger } from '../../utils/logger'

const logger = createLogger('useAuthHeader')

export const useAuthHeader = () => {
  const location = useLocation()
  const navigate = useNavigate()

  // URL에서 토큰 처리
  const urlParams = new URLSearchParams(window.location.search)
  const token = urlParams.get('token')

  // Store hooks
  const set = useUserStore((state: any) => state.set)
  const prevSet = usePrevStore((state: any) => state.set)
  const setRefetch = useUserStore((state: any) => state.setRefetch)
  const setPlanRefetch = usePlanStore((state: any) => state.setRefetch)
  const setPlan = usePlanStore((state: any) => state.set)

  // Query hooks
  const { mutate: logoutMutate } = useLogOut()
  const { data: userData, refetch } = useGetMe()
  const { data: userPlanData, refetch: planRefetch } = useGetMePlan()

  // 토큰 처리 및 URL 정리
  useEffect(() => {
    if (token) {
      localStorage.setItem('token', token)
      const searchParams = new URLSearchParams(location.search)
      searchParams.delete('token')
      const baseUrl = location.pathname
      const modifiedUrl = `${baseUrl}${
        searchParams.toString() ? '?' + searchParams.toString() : ''
      }`
      navigate(modifiedUrl, { replace: true })
    }
  }, [token, location.search, location.pathname, navigate])

  // 가이드 샘플 로컬스토리지 처리
  useEffect(() => {
    const sample = getLocalStorage('guide_sample')
    if (!sample) {
      saveLocalStorage('guide_sample', '1')
    }
  }, [])

  // 사용자 데이터를 스토어에 설정
  useEffect(() => {
    if (userData?.userInfo) {
      set({ userInfo: userData })
    }
  }, [userData, set])

  // 사용자 플랜 데이터를 스토어에 설정
  useEffect(() => {
    if (userPlanData) {
      setPlan({ userPlanInfo: userPlanData })
    }
  }, [userPlanData, setPlan])

  // refetch 함수들을 스토어에 설정
  useEffect(() => {
    if (refetch && typeof refetch === 'function') {
      setRefetch(refetch)
    } else {
      logger.info('Refetch not ready:', refetch)
    }

    if (planRefetch && typeof planRefetch === 'function') {
      setPlanRefetch(planRefetch)
    } else {
      logger.info('planRefetch not ready:', planRefetch)
    }
  }, [refetch, setRefetch, planRefetch, setPlanRefetch])

  // 이전 URL 저장 및 약관 미동의 시 리다이렉트
  useEffect(() => {
    const { pathname } = location

    // 특정 페이지가 아닐 때만 이전 URL 저장
    if (
      pathname !== '/sign-in' &&
      pathname !== '/sign-up' &&
      pathname !== '/terms'
    ) {
      prevSet({ prevUrl: pathname })
    }

    // 약관 미동의 시 리다이렉트
    if (userData?.userInfo && !userData?.userInfo.termsAccepted) {
      const userType = urlParams.get('userType')
      navigate(`/terms?userType=${userType}`)
    }
  }, [location, userData?.userInfo, navigate, prevSet, urlParams])

  // 확장 프로그램 설치 감지
  useEffect(() => {
    const node = document.getElementById('fastcut-extension-installed')

    if (node?.dataset.installed === 'true') {
      logger.info('✅ Fastcut 확장 프로그램 설치 감지 (DOM)')
      saveLocalStorage('fs_extension', true)
    } else {
      logger.info('✅ Fastcut 확장 프로그램 설치 감지 (DOM) 실패')
      saveLocalStorage('fs_extension', false)
    }

    const handler = (event: any) => {
      logger.info('✅ Fastcut 익스텐션 설치 이벤트 감지!', event.detail)
      saveLocalStorage('fs_extension', true)
    }

    window.addEventListener('FASTCUT_EXTENSION_INSTALLED', handler)

    return () => {
      window.removeEventListener('FASTCUT_EXTENSION_INSTALLED', handler)
    }
  }, [location.pathname])

  // 사용자 타입에 따른 권한 확인
  const hasPermission = (requiredUserTypes: UserType[]) => {
    if (!userData?.userType) return false
    return requiredUserTypes.includes(userData.userType as UserType)
  }

  // 자동 업로드 권한 확인 (기존 로직)
  const hasYoutubeAuthPermission = () => {
    if (!userData?.userType) return false

    return (
      userData.userType === UserType.프로 ||
      userData.userType === UserType.프리미엄 ||
      userData.userType === UserType.체험 ||
      userData.userType === UserType['숏폼 스페셜']
    )
  }

  // 챌린지 한달 남은사람들 선별해서 구글폼 띄워주기
  const isFinishChallenge = () => {
    // 예시 형식 2025-10-10
    const endate = userData?.subscriptions?.endDate
    const isChallenge = !!userData?.challenge

    if (!isChallenge || !endate) return false

    const endDateObj = new Date(endate)
    const today = new Date()
    const oneMonthFromNow = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000)

    return endDateObj <= oneMonthFromNow && endDateObj >= today
  }

  const handleLogout = () => {
    onLogOut()
  }

  return {
    userData,
    userPlanData,

    isLoggedIn: !!userData?.userInfo,
    hasPermission,
    hasYoutubeAuthPermission,
    isFinishChallenge,
    handleLogout,
    currentPath: location.pathname,
  }
}
